import { InfoIcon } from "@/components/icons/info-icon";
import type { ChatMessagePlanFoodsCommitted } from "@/stores/chat.store";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { addDays, format, isSameDay, isSameYear, parseISO } from "date-fns";
import { memo, useState, type ReactNode } from "react";

interface PlanFoodsCommittedCardProps {
  committed: ChatMessagePlanFoodsCommitted;
  actions?: ReactNode;
}

const formatNutritionNumber = (value: number): string =>
  new Intl.NumberFormat("en-GB", { maximumFractionDigits: 1 }).format(value);

export const formatCommittedPlanHeading = (targetDate: string, now = new Date()): string => {
  const date = parseISO(targetDate);
  if (isSameDay(date, now)) return "Added to your plan for today:";
  if (isSameDay(date, addDays(now, 1))) return "Added to your plan for tomorrow:";
  const dateFormat = isSameYear(date, now) ? "EEEE d MMMM" : "EEEE d MMMM yyyy";
  return `Added to your plan for ${format(date, dateFormat)}:`;
};

const ChevronRight = () => (
  <svg
    width="5"
    height="10"
    viewBox="0 0 5 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M1 1L4 5L1 9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Figma: 12px regular #aca5e0
const MACRO_HEADER_CLASS = "text-center text-[12px] font-normal text-[#aca5e0]";
// Figma: 14px semibold white
const MACRO_VALUE_CLASS = "text-[14px] font-semibold text-center text-white";

export const PlanFoodsCommittedCard = memo(function PlanFoodsCommittedCard({
  committed,
  actions,
}: PlanFoodsCommittedCardProps) {
  const { day } = committed;
  const [showNutrition, setShowNutrition] = useState(false);
  const [showTodayNutrition, setShowTodayNutrition] = useState(false);

  const showEnergy = day.remaining_energy_kcal !== null;
  const showProtein = day.remaining_protein_grams !== null;
  const hasRemaining = showEnergy || showProtein;

  const totalKcal = day.items.reduce((sum, item) => sum + item.energy_kcal, 0);
  const totalProtein = day.items.reduce((sum, item) => sum + item.protein_grams, 0);
  const itemCount = day.items.length;

  const showCarbs = showNutrition && day.items.every((i) => i.carbs_grams != null);
  const totalCarbs = day.items.reduce((sum, item) => sum + (item.carbs_grams ?? 0), 0);

  const proteinGoal = day.protein_goal_grams ?? null;
  const showProteinRing = proteinGoal != null && proteinGoal > 0;
  const ringFraction = showProteinRing
    ? Math.min(1, Math.max(0, (proteinGoal - (day.remaining_protein_grams ?? 0)) / proteinGoal))
    : 0;
  const ringDashoffset = 2 * Math.PI * 20 * (1 - ringFraction);

  const gridCols = showNutrition
    ? showCarbs
      ? "grid-cols-[1fr_44px_44px_44px]"
      : "grid-cols-[1fr_44px_44px]"
    : "grid-cols-[1fr_44px]";

  return (
    <section
      data-presentation="plan-foods-committed"
      data-basket-id={committed.basketId}
      className="flex w-full flex-col gap-3"
      aria-label={formatCommittedPlanHeading(day.target_date)}
    >
      {/* Card */}
      <div className="w-full overflow-hidden rounded-[18px] border border-[rgba(239,234,230,0.06)] bg-[#191635]">

        {/* Column header row */}
        <div className={cn("grid items-center px-3 py-2.5", gridCols)}>
          <div className="flex items-center gap-1.5">
            <span className="size-[22px] shrink-0" aria-hidden>
              <img
                src="/assets/icons/plan-foods-committed.svg"
                alt=""
                className="size-[22px]"
                width={22}
                height={22}
              />
            </span>
            <span className="text-[14px] font-medium text-white">
              {itemCount} item{itemCount !== 1 ? "s" : ""} logged
            </span>
          </div>
          <span className={MACRO_HEADER_CLASS}>Kcal</span>
          {showNutrition && <span className={MACRO_HEADER_CLASS}>Protein</span>}
          {showCarbs && <span className={MACRO_HEADER_CLASS}>Carbs</span>}
        </div>

        {/* Meal item rows */}
        {day.items.map((item) => (
          <div
            key={`${item.meal_id}:${item.meal_time}`}
            className={cn("grid items-center border-t border-white/[0.08] px-3 py-3", gridCols)}
          >
            <div className="min-w-0 pr-2">
              <p className="min-w-0 truncate text-[14px] font-semibold leading-[24px] text-white">
                {item.name}
              </p>
              <p className="min-w-0 truncate text-[12px] font-normal text-[#aca5e0]">
                {item.portion_size}
              </p>
            </div>
            <span className={MACRO_VALUE_CLASS}>
              {formatNutritionNumber(item.energy_kcal)}
            </span>
            {showNutrition && (
              <span className={MACRO_VALUE_CLASS}>
                {formatNutritionNumber(item.protein_grams)}
              </span>
            )}
            {showCarbs && (
              <span className={MACRO_VALUE_CLASS}>
                {formatNutritionNumber(item.carbs_grams as number)}
              </span>
            )}
          </div>
        ))}

        {/* Footer: collapsed summary OR expanded total */}
        {!showNutrition ? (
          <div className="flex items-center gap-3 border-t border-white/[0.08] bg-[#241d47] px-3 py-3">
            <div className="flex min-w-0 flex-col">
              <span className="text-[14px] font-semibold text-white">
                {formatNutritionNumber(totalKcal)} kcal added
              </span>
              <span className="text-[12px] font-normal text-[#aca5e0]">
                {formatNutritionNumber(totalProtein)}g protein
              </span>
            </div>
            <button
              type="button"
              onClick={() => setShowNutrition(true)}
              className="ml-auto flex shrink-0 items-center gap-1 text-[12px] font-semibold text-[#aca5e0] active:opacity-70"
            >
              View nutrition
              <ChevronRight />
            </button>
          </div>
        ) : (
          <div className={cn("grid items-center border-t border-white/[0.08] bg-[#241d47] px-3 py-3", gridCols)}>
            <span className="text-[14px] font-semibold text-white">Total</span>
            <span className={cn(MACRO_VALUE_CLASS, "font-bold")}>
              {formatNutritionNumber(totalKcal)}
            </span>
            <span className={cn(MACRO_VALUE_CLASS, "font-bold")}>
              {formatNutritionNumber(totalProtein)}
            </span>
            {showCarbs && (
              <span className={cn(MACRO_VALUE_CLASS, "font-bold")}>
                {formatNutritionNumber(totalCarbs)}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Action row: passed action buttons + Today's nutrition toggle */}
      {(actions != null || hasRemaining) && (
        <div className="flex items-center gap-6 pl-3">
          {actions}
          {hasRemaining && (
            <button
              type="button"
              onClick={() => setShowTodayNutrition((v) => !v)}
              className={cn(
                "flex h-11 shrink-0 items-center gap-[5px] text-white active:text-white/70",
                textVariants.footnote,
              )}
            >
              <InfoIcon className="size-[21px] fill-white" />
              Today&apos;s nutrition
            </button>
          )}
        </div>
      )}

      {/* Today's nutrition panel */}
      {showTodayNutrition && hasRemaining && (
        <div className="w-full border-y border-[rgba(255,255,255,0.08)] px-3 pt-3 pb-[18px]">
          <div className="flex items-center justify-between">
            {showEnergy && (
              <div className="flex flex-col gap-[4px]">
                <span className="text-[12px] font-normal leading-[18px] text-[#aca5e0]">
                  Calories left today
                </span>
                <p className="flex items-baseline gap-1">
                  <span className="text-[32px] font-bold leading-[24px] text-white">
                    {formatNutritionNumber(
                      day.over_energy_target
                        ? Math.abs(day.remaining_energy_kcal as number)
                        : (day.remaining_energy_kcal as number),
                    )}
                  </span>
                  <span className="text-[18px] leading-[24px] text-[#aca5e0]">
                    {day.over_energy_target ? "kcal over" : "kcal"}
                  </span>
                </p>
              </div>
            )}

            {showProtein && (
              <div className="flex shrink-0 items-center gap-4">
                {showProteinRing && (
                  <div className="relative size-12" aria-hidden data-testid="protein-ring">
                    <svg viewBox="0 0 48 48" className="absolute inset-0 size-12 -rotate-90">
                      <circle
                        cx="24"
                        cy="24"
                        r="20"
                        fill="none"
                        stroke="rgba(255,255,255,0.1)"
                        strokeWidth="3"
                      />
                      <circle
                        cx="24"
                        cy="24"
                        r="20"
                        fill="none"
                        stroke="var(--color-os-brand)"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeDasharray={`${2 * Math.PI * 20}`}
                        strokeDashoffset={`${ringDashoffset}`}
                        data-testid="protein-ring-fill"
                      />
                    </svg>
                  </div>
                )}
                <div className="flex w-[60px] flex-col gap-[2px]">
                  <span className="text-[12px] font-normal leading-[18px] text-[#aca5e0]">Protein</span>
                  <span className="text-[26px] font-bold leading-[24px] text-white">
                    {formatNutritionNumber(day.remaining_protein_grams as number)}g
                  </span>
                  <span className="text-[12px] font-normal leading-[18px] text-[#aca5e0]">remaining</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
});
